import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { handleUnauthorized } from '../../TokenAuth/auth';

const initialState = {
  videos: [],
  status: 'idle',
  error: null,
  loading: false,
  mainTitle: "What our user say",
  pagination: {
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
  },
};

// Fetch Videos
export const fetchVideos = createAsyncThunk(
  'videos/fetchVideos',
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("astroVani-admin-token");

      const url = `${process.env.REACT_APP_BASEURL}/admin/video?page=${page}&limit=${limit}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        handleUnauthorized();
        throw new Error("Unauthorized: Please login again");
      }

      if (!response.ok) {
        throw new Error('Failed to fetch videos');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Post a new video
export const postVideo = createAsyncThunk(
  'videos/postVideo',
  async (videoData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("astroVani-admin-token");

      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/video`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(videoData),
      });

      if (response.status === 401) {
        handleUnauthorized();
        throw new Error("Unauthorized: Please login again");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add video');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update a video
export const updateVideo = createAsyncThunk(
  'videos/updateVideo',
  async ({ id, videoData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("astroVani-admin-token");

      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/video/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(videoData),
      });

      if (response.status === 401) {
        handleUnauthorized();
        throw new Error("Unauthorized: Please login again");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update video');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete a video
export const deleteVideo = createAsyncThunk(
  'videos/deleteVideo',
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("astroVani-admin-token");

      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/video?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        handleUnauthorized();
        throw new Error("Unauthorized: Please login again");
      }

      if (!response.ok) {
        throw new Error('Failed to delete video');
      }

      const data = await response.json();
      return { id, data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const videoSlice = createSlice({
  name: 'videos',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVideos.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        state.videos = action.payload?.data || [];
        state.pagination = action.payload?.paginationDetail || initialState.pagination;
        // Store mainTitle from response if available
        if (action.payload?.mainTitle) {
          state.mainTitle = action.payload.mainTitle;
        }
      })
      .addCase(fetchVideos.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(postVideo.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(postVideo.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        if (action.payload?.data) {
          state.videos.push(action.payload.data);
        }
      })
      .addCase(postVideo.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateVideo.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(updateVideo.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        if (action.payload?.data) {
          const index = state.videos.findIndex(v => v._id === action.payload.data._id);
          if (index !== -1) {
            state.videos[index] = action.payload.data;
          }
        }
      })
      .addCase(updateVideo.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteVideo.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        state.videos = state.videos.filter(v => v._id !== action.payload.id);
      })
      .addCase(deleteVideo.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default videoSlice.reducer;

